import React from 'react';
import { Share2, Facebook, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string;
}

export default function SocialShare({ url, title, description, imageUrl }: SocialShareProps) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = description ? encodeURIComponent(description) : '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`
  };

  const handleShare = async (platform: string) => {
    const shareUrl = shareLinks[platform as keyof typeof shareLinks];
    
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('¡Enlace copiado!');
      } catch (error) {
        toast.error('Error al copiar el enlace');
      }
      return;
    }

    // Abrir en una nueva ventana centrada
    const width = 600;
    const height = 400;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;
    
    window.open(
      shareUrl,
      'Compartir',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="flex items-center space-x-2 text-gray-600">
        <Share2 className="h-4 w-4" />
        <span className="text-sm font-medium">Compartir este árbol</span>
      </div>
      
      <div className="flex space-x-4">
        <button
          onClick={() => handleShare('facebook')}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
          title="Compartir en Facebook"
        >
          <Facebook className="h-5 w-5" />
        </button>

        <button
          onClick={() => handleShare('twitter')}
          className="p-2 text-black hover:bg-gray-50 rounded-full transition-colors"
          title="Compartir en X (Twitter)"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </button>

        <button
          onClick={() => handleShare('whatsapp')}
          className="p-2 text-[#25D366] hover:bg-green-50 rounded-full transition-colors"
          title="Compartir en WhatsApp"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </button>

        <button
          onClick={() => handleShare('copy')}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          title="Copiar enlace"
        >
          <Link2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}